import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAccess } from '../../app/providers/AccessProvider.jsx';
import { useSession } from '../../app/providers/SessionProvider.jsx';
import { loadMembershipPlans } from '../../services/account/accountService.js';
import { withOpenAICampaignParams } from '../../services/ads/openaiMeasurement.js';
import { validMembershipPlans, membershipPath, formatPrice } from '../../services/commerce/offerPresentation.js';
import '../../styles/offers.css';

export function useMembershipPlans() {
  const [state, setState] = useState({ plans: [], loading: true, error: '' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setState({ plans: [], loading: true, error: '' });
    loadMembershipPlans().then((rows) => {
      const plans = validMembershipPlans(rows);
      if (!plans.length) throw new Error('Current membership plans are unavailable.');
      if (active) setState({ plans, loading: false, error: '' });
    }).catch(() => { if (active) setState({ plans: [], loading: false, error: 'We could not load current membership prices. Please try again.' }); });
    return () => { active = false; };
  }, [attempt]);
  return { ...state, retry: () => setAttempt((value) => value + 1) };
}

export function MembershipPlans({ campaign = false }) {
  const { user } = useSession();
  const access = useAccess();
  const { plans, loading, error, retry } = useMembershipPlans();
  if (user && access.subscription.active) return <div className="offer-plans" id="membership-plans"><p>Your membership is active. <NavLink to="/profile/membership">Manage membership</NavLink></p></div>;
  if (user && (access.loading || access.error)) return <p role="status">{access.error ? "Membership status could not be verified. Open your account before starting another subscription." : "Checking your membership…"}</p>;
  return <div className="offer-plans" id="membership-plans">
    {loading ? <p role="status">Loading current membership prices…</p> : null}
    {error ? <div role="alert"><p>{error}</p><button type="button" onClick={retry}>Try again</button></div> : null}
    <div className="membership-grid">{plans.map((plan) => {
      const annual = plan.id === 'annual';
      const path = membershipPath(plan.id, Boolean(user));
      return <article className="membership-plan" key={plan.id}>
        <span className="eyebrow">{annual ? 'Annual' : 'Monthly'}</span>
        <h2>{plan.name || `WatchVIM ${annual ? 'Annual' : 'Monthly'}`}</h2>
        <div className="membership-price">{formatPrice(plan.price_usd ?? plan.price, plan.currency)}<small> / {annual ? 'year' : 'month'}</small></div>
        <p>Billed {annual ? 'annually' : 'monthly'}. Renews automatically until canceled.</p>
        <p>Ad-free access to membership titles. Rental-only titles, when offered, are separate.</p>
        <NavLink className="account-primary inline offer-plan-button" to={campaign ? withOpenAICampaignParams(path) : path}>Choose {annual ? 'Annual' : 'Monthly'}</NavLink>
      </article>;
    })}</div>
    {plans.length ? <p className="offer-fineprint">Cancel future renewal in your membership settings. Access continues through your paid period. <NavLink to="/terms">Terms</NavLink> · <NavLink to="/refund-policy">Refund policy</NavLink></p> : null}
  </div>;
}

export function DeviceGuide() {
  return <section className="device-guide" aria-labelledby="device-guide-title">
    <h2 id="device-guide-title">How to watch</h2>
    <p>Use the same WatchVIM account on supported devices. App availability varies by device and region.</p>
    <div className="device-guide-grid">
      <article><h3>Web browser</h3><p>Sign in at WatchVIM.com on your computer, phone or tablet, then choose a title.</p><NavLink to="/home">Browse WatchVIM</NavLink></article>
      <article><h3>iPhone, iPad &amp; Apple TV</h3><p>Install WatchVIM from the App Store and sign in. If your TV displays an activation code, follow its on-screen instructions.</p><a href="https://apps.apple.com/us/app/watchvim/id1558094333" target="_blank" rel="noopener noreferrer">Get the Apple app</a></article>
      <article><h3>Android</h3><p>Open Google Play on your device to check compatibility and install WatchVIM, then sign in.</p><a href="https://play.google.com/store/apps/details?id=tv.uscreen.watchvim" target="_blank" rel="noopener noreferrer">Get the Android app</a></article>
    </div>
    <p className="offer-fineprint">App-store billing offers can differ from website plans. For another TV or a setup question, <NavLink to="/support">contact support</NavLink> before subscribing.</p>
  </section>;
}
